import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Box, Button, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import { useLocation } from 'react-router-dom';
import { logo_new } from '../assets';
import eng from '../../public/locales/eng.json';

const strings = eng as Record<string, string>;
const OFFLINE_EXEMPT_PATHS = ['/privacy-policy'];

type NetworkContextValue = {
  assertOnlineForApi: () => boolean;
};

const NetworkContext = createContext<NetworkContextValue | null>(null);

const isOfflineExemptPath = (pathname: string): boolean =>
  OFFLINE_EXEMPT_PATHS.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  );

const OfflineScreen: React.FC = () => (
  <Box
    sx={{
      width: '100%',
      minHeight: '100dvh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      overflow: 'hidden',
      px: 3,
      textAlign: 'center',
      backgroundColor: 'var(--color-background, #ffffff)',
    }}
  >
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        zIndex: 2,
        maxWidth: 600,
        width: '100%',
      }}
    >
      <Box
        component="img"
        src={logo_new}
        alt="HL Skin"
        sx={{
          width: { xs: '70%', sm: '60%', md: '320px' },
          maxWidth: 360,
          height: 'auto',
          objectFit: 'contain',
        }}
      />
      <WifiOffIcon sx={{ fontSize: 56, color: 'var(--color-text-secondary, #666)' }} />
      <Typography variant="h5" sx={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>
        {strings.Txt1002 ?? 'No internet connection'}
      </Typography>
      <Typography variant="body1" sx={{ maxWidth: 420, color: 'var(--color-text-secondary, #666)' }}>
        {strings.Txt1003 ?? 'Please check your Wi-Fi or mobile data and try again.'}
      </Typography>
      <Button
        variant="contained"
        startIcon={<RefreshIcon />}
        onClick={() => window.location.reload()}
        sx={{
          mt: 2,
          borderRadius: '8px',
          background: 'var(--color-primary)',
          color: 'var(--color-text-inverse)',
          textTransform: 'none',
          fontWeight: 700,
          px: 3,
          py: 1.5,
          '&:hover': {
            background: '#333333',
          },
        }}
      >
        {strings.Txt1004 ?? 'Refresh'}
      </Button>
    </Box>
  </Box>
);

/** Wrap the app — blocks UI when the device is offline. */
const NetworkGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [isOnline, setIsOnline] = useState(
    () => typeof navigator === 'undefined' || navigator.onLine,
  );

  useEffect(() => {
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  const assertOnlineForApi = useCallback(() => {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      setIsOnline(false);
      return false;
    }
    return true;
  }, []);

  const contextValue = useMemo(
    () => ({ assertOnlineForApi }),
    [assertOnlineForApi],
  );

  if (!isOnline && !isOfflineExemptPath(location.pathname)) {
    return (
      <NetworkContext.Provider value={contextValue}>
        <OfflineScreen />
      </NetworkContext.Provider>
    );
  }

  return (
    <NetworkContext.Provider value={contextValue}>
      {children}
    </NetworkContext.Provider>
  );
};

export const useNetworkGate = (): NetworkContextValue => {
  const ctx = useContext(NetworkContext);
  if (!ctx) {
    throw new Error('useNetworkGate must be used within NetworkGate');
  }
  return ctx;
};

export default NetworkGate;
